import React from "react";
import Co2StateDTO from "src/models/Co2StateDTO";
import GrainAnalyticsModuleStateDTO from "src/models/GrainAnalyticsModuleStateDTO";
import RangeFinderStateDTO from "src/models/RangeFinderStateDTO";
import SensorReading from "./SensorReading";

interface HeadspaceStateViewProps {
    gamStates: GrainAnalyticsModuleStateDTO[] | null | undefined;
}

type Co2WithStatus = Co2StateDTO & { statusCodeRaw?: number | null };
type RangeFinderWithStatus = RangeFinderStateDTO & { statusCodeRaw?: number | null };
type GamWithAir = GrainAnalyticsModuleStateDTO & {
    rhPercent?: number | null;
    mcPercent?: number | null;
    tempF?: number | null;
    co2State: Co2WithStatus | null;
    rangeFinderState: RangeFinderWithStatus | null;
};

const fmt = (value: number | null | undefined, digits = 1, suffix = ''): string =>
    value == null ? '--' : `${value.toFixed(digits)}${suffix}`;

const HeadspaceStateView: React.FC<HeadspaceStateViewProps> = ({ gamStates }) => {
    const gams = (gamStates ?? []) as GamWithAir[];

    const textColor = '#000';
    const borderColor = '#d9d9d9';

    const th: React.CSSProperties = {
        textAlign: 'center',
        fontWeight: 600,
        padding: '4px 10px',
        borderBottom: `1px solid ${borderColor}`,
        fontSize: 12,
        color: textColor,
    };
    const label: React.CSSProperties = {
        textAlign: 'left',
        fontWeight: 500,
        padding: '3px 8px',
        fontSize: 12,
        color: textColor,
        whiteSpace: 'nowrap',
    };
    const cell: React.CSSProperties = {
        textAlign: 'center',
        padding: '3px 10px',
        fontFamily: 'monospace',
        fontSize: 12,
        color: textColor,
    };

    if (gams.length === 0) {
        return <span style={{ color: '#aaa', fontSize: 12 }}>No headspace data</span>;
    }

    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                    <tr>
                        <th style={{ ...th, textAlign: 'left' }}></th>
                        {gams.map(g => (
                            <th key={g.number} style={th}>GAM {g.number}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style={label}>CO₂</td>
                        {gams.map(g => (
                            <td key={g.number} style={cell}>
                                <SensorReading
                                    value={fmt(g.co2State?.co2Ppm, 0, ' ppm')}
                                    code={g.co2State?.statusCodeRaw}
                                />
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td style={label}>Temp</td>
                        {gams.map(g => (
                            <td key={g.number} style={cell}>
                                {fmt(g.tempF, 1, '°F')}
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td style={label}>RH</td>
                        {gams.map(g => (
                            <td key={g.number} style={cell}>
                                {fmt(g.rhPercent, 0, '%')}
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td style={label}>MC</td>
                        {gams.map(g => (
                            <td key={g.number} style={cell}>
                                {fmt(g.mcPercent, 1, '%')}
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td style={label}>Sonar Dist</td>
                        {gams.map(g => (
                            <td key={g.number} style={cell}>
                                {g.rangeFinderState != null
                                    ? <SensorReading
                                        value={fmt(g.rangeFinderState.distanceFt, 1, ' ft')}
                                        code={g.rangeFinderState.statusCodeRaw}
                                      />
                                    : <span style={{ color: '#aaa' }}>N/A</span>}
                            </td>
                        ))}
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default HeadspaceStateView;
